/**
 * Si un rol llega a una pantalla.
 *
 * El panel admin no tiene una matriz de capacidades: tiene **una lista de
 * menú por rol** (`navigationByRole` en `src/config/navigation.tsx`), y esa
 * lista es la que decide qué ve cada quien. Las guías se filtran contra
 * ella y no contra una segunda lista propia — mantener dos listas de roles
 * en paralelo es exactamente cómo se desincronizan.
 *
 * **No sustituye a la autorización**, que vive en el backend. Sirve para no
 * ofrecerle a un `SUPPORT_AGENT` una guía de una pantalla que su menú no
 * tiene: la guía navegaría a una ruta que `ProtectedRoute` le rebota.
 */

import { navigationByRole } from '@/config/navigation';
import type { StaffRole } from '@/types/staff.types';

/**
 * Las rutas del menú de un rol, global y por país juntas, sin las cabeceras
 * de sección.
 */
export function navHrefsForRole(role: StaffRole | undefined): string[] {
  if (!role) return [];
  const nav = navigationByRole[role];
  if (!nav) return [];

  return [...nav.global, ...nav.country]
    .filter((item) => !item.section && !!item.href)
    .map((item) => item.href as string);
}

/**
 * Si el rol tiene sección «Operación por País» en su menú.
 *
 * El asesor comercial externo no la tiene: su menú es sólo su cartera. Un
 * paso que le explicara el selector de país se quedaría esperando un ancla
 * que nunca se monta, así que se descarta antes de intentarlo.
 */
export function roleHasCountryNav(role: StaffRole | undefined): boolean {
  if (!role) return false;
  return (navigationByRole[role]?.country.length ?? 0) > 0;
}

/**
 * Si el menú del rol incluye esa ruta, o la pantalla de la que cuelga.
 *
 * El menú lista `/companies`, no `/companies/:id`: una guía que enseñe el
 * detalle declara la ruta del listado como `href` y navega al detalle en sus
 * pasos. Se acepta además una subruta **de pestaña** (`/gifts/puntos` cuelga
 * de `/gifts`, 2026-10-15): «Regalos» lleva el tipo y la pestaña en la ruta y
 * cada uno tiene su guía. Sólo en el corte de la barra — `/gifts` no alcanza
 * `/giftsx`.
 */
export function roleCanReach(role: StaffRole | undefined, href: string): boolean {
  return navHrefsForRole(role).some(
    (navHref) => href === navHref || href.startsWith(`${navHref}/`),
  );
}
